import React from 'react';
import { connect } from 'react-redux';
import { css } from 'styled-components';
import StyledWrapper from '@insite/client-framework/Common/StyledWrapper';
import siteMessage from '@insite/client-framework/SiteMessage';
import { getSession } from '@insite/client-framework/Store/Context/ContextSelectors';
import translate from '@insite/client-framework/Translate';
import Button from '@insite/mobius/Button';
import Link, { LinkPresentationProps } from '@insite/mobius/Link';
import Modal, { ModalPresentationProps } from '@insite/mobius/Modal';
import Typography from '@insite/mobius/Typography';
import InjectableCss from '@insite/mobius/utilities/InjectableCss';
import CrossIcon from '@NA/Icons/CrossIcon';
import { pushDealerAuthDataToDataLayer } from '@NA/Overrides/Common/Utilities/CustomTracking';
import CustomApplicationState from '@NA/Store/Data/CustomApplicationState/CustomApplicationState';
import { CoveoProductModel } from '@NA/Types/CoveoModel';
import ADIColors from '@NA/Utils/ADIColors';

interface OwnProps {
    open: boolean;
    coveoProduct: CoveoProductModel | undefined;
    onClose: () => void;
}

const mapStateToProps = (state: CustomApplicationState) => {
    let customerNumber = "";
    const session = getSession(state);
    const billToId = session?.billToId;
    if (billToId) {
        customerNumber = state.data.billTos.byId[billToId]?.customerNumber;
    }

    return {
        session,
        customerNumber,
    };
};

export interface CoveoDealerCertModalStyles {
    modal?: ModalPresentationProps;
    messageText?: InjectableCss;
    link?: LinkPresentationProps;
    button?: InjectableCss;
}

export const coveoDealerCertModalStyles: CoveoDealerCertModalStyles = {
    modal: {
        sizeVariant: "medium",
        closeButtonIconProps: {
            src: CrossIcon,
        },
        cssOverrides: {
            modalBody: css`
                max-width: 450px !important;
                border-radius: 4px;
                padding: 40px 25px !important;
            `,
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                border-bottom: 0;
                padding: 0 !important;
                margin-top: 5px;
                margin-bottom: 10px;
                > h4 {
                    font-size: 26px;
                    font-weight: bold;
                    font-family: "Nunito Sans", sans-serif;
                    line-height: normal;
                    color: ${ADIColors.darkBlue1};
                }
                button {
                    border-radius: 0;
                    svg {
                        height: 20px;
                    }
                }
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
            titleButton: css`
                position: absolute;
                top: 10px;
                right: 10px;
                padding: 5px;
                span {
                    top: 0 !important;
                }
            `,
        },
    },
    messageText: {
        css: css`
            font-family: "Nunito Sans" !important;
            font-size: 16px;
            font-weight: normal;
            line-height: 1;
            color: #2d2926;
        `,
    },
    button: {
        css: css`
            padding-top: 25px;
            button {
                width: auto;
            }
        `,
    },
};

const styles = coveoDealerCertModalStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const CoveoDealerCertModal = ({ open, coveoProduct, session, customerNumber, onClose }: Props) => {
    const clickHandler = () => {
        pushDealerAuthDataToDataLayer(
            coveoProduct?.brandName ?? "",
            coveoProduct?.vendorAuthRequiredLink ?? "",
            customerNumber,
            "Product List",
            session,
        );
    };

    return (
        <Modal
            {...styles.modal}
            headline={translate("Dealer Certification Required")}
            isOpen={open}
            handleClose={onClose}
        >
            {coveoProduct?.vendorAuthRequiredList?.toLowerCase() === "true" ? (
                <Typography {...styles.messageText}>
                    {siteMessage("Dealer_Vendor_Certification_Message")}{" "}
                    <Link
                        href={coveoProduct.vendorAuthRequiredLink}
                        target="_blank"
                        {...styles.link}
                        onClick={clickHandler}
                    >
                        {translate(" vendor's form.")}
                    </Link>{" "}
                    {translate("For more information, call ")}
                    <StyledWrapper as="span">1.877.228.6739.</StyledWrapper>
                </Typography>
            ) : (
                <Typography {...styles.messageText}>{siteMessage("Dealer_Certification_Message")}</Typography>
            )}
            <StyledWrapper {...styles.button}>
                <Button onClick={onClose}>Continue Shopping</Button>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps)(CoveoDealerCertModal);
